import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Product, AddToCartEvent } from '../../../../shared/models/product.model';
import { Category } from '../../../../shared/models/category.model';
import {
  CategoryProductsTab,
  TabbedCarouselBlockComponent,
} from '../tabbed-carousel-block/tabbed-carousel-block.component';
import { PublicCategory, PublicSubcategorySection } from '../../data/home-blocks.model';

export interface PerronSinglesBlockData {
  parent: PublicCategory | null;
  limit_per_subcategory: number;
  sections: PublicSubcategorySection[];
  // Productos ya mapeados a `Product` UI por el padre (HomeComponent), alineados
  // 1:1 con `sections`. Espeja el contrato del `NewReleasesBlockComponent`.
  sectionsUiProducts: Product[][];
}

const FALLBACK_PARENT = {
  id: -1,
  name: 'Perrón Singles',
  slug: 'perron-singles',
  cover: null,
} as const;

/**
 * Bloque del home asociado al type `subcategories-carousel` del API. Un tab
 * por subcategoría que aporte productos — el backend ya filtra secciones
 * vacías antes de enviar. Render dumb vía `TabbedCarouselBlockComponent`,
 * mismo patrón que `NewReleasesBlockComponent`.
 */
@Component({
  selector: 'app-perron-singles-block',
  imports: [TabbedCarouselBlockComponent],
  template: `
    <app-tabbed-carousel-block
      [title]="title()"
      ariaLabel="Filtrar por subcategoría"
      viewAllLabel="Ver catálogo"
      [viewAllLink]="viewAllLink()"
      [tabs]="tabs()"
      [shouldRender]="shouldRender()"
      (addToCart)="addToCart.emit($event)"
      (zoomOpen)="zoomOpen.emit($event)"
      (notifyRequest)="notifyRequest.emit($event)"
      (notifyCancel)="notifyCancel.emit($event)"
      (cardClick)="cardClick.emit($event)"
      (reserveClick)="reserveClick.emit($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerronSinglesBlockComponent {
  readonly data = input<PerronSinglesBlockData | null>(null);

  private readonly parent = computed<typeof FALLBACK_PARENT | PublicCategory>(
    () => this.data()?.parent ?? FALLBACK_PARENT,
  );

  protected readonly title = computed(() => this.parent().name);
  protected readonly viewAllLink = computed(() => {
    const slug = this.parent().slug;
    return slug ? `/tienda/${slug}` : '/tienda';
  });

  // Un tab por section. Los productos ya vienen mapeados desde el padre vía
  // `sectionsUiProducts` para no re-adaptar en cada change-detection.
  protected readonly tabs = computed<CategoryProductsTab[]>(() => {
    const value = this.data();
    if (!value) return [];
    return value.sections.map((section, idx): CategoryProductsTab => ({
      category: toUiCategory(section.category),
      products: value.sectionsUiProducts[idx] ?? [],
    }));
  });

  protected readonly shouldRender = computed(() => this.tabs().length > 0);

  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  reserveClick = output<Product>();
}

function toUiCategory(c: PublicCategory | typeof FALLBACK_PARENT): Category {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug ?? '',
    description: null,
    cover: c.cover,
    is_expansion: false,
    parent_id: null,
  };
}
